import moment from 'moment-timezone';
import { NodeMetricKind } from '@scale/shared/dailyPlan/metrics/nodeMetrics';
import { ProjectGroupDiff, UpdateProjectGroupRequest } from '@scale/shared/dailyPlan/types/api';
import _ from 'lodash';
import { MetricRowData } from 'react-scale/corp/views/DailyPlanning/DailyPlanning/components/MetricRow';
import { MetricFilters } from 'react-scale/corp/views/DailyPlanning/DailyPlanning/components/utils';
import { DISABLED_METRIC_ROW_OVERRIDES } from './DailyPlanning/defaults';
import { PipelineConfigMapping } from '@scale/shared/dailyPlan/queueConfigs';
import {
  IDailyPlanProjectGroup,
  ProjectGroupLifecyle,
} from '@scale/shared/dailyPlan/dailyPlanProjectGroup';

// ---- Non-Task attempt egress transitions
export const canDisplayMetric = (
  metric: MetricRowData,
  metricFilters: MetricFilters,
  isHidden: boolean,
  nodeName: NodeMetricKind,
): boolean =>
  (!isHidden || (metricFilters.showHidden && isHidden)) &&
  ((metricFilters.showPlanning && metric.isPlanning) ||
    (metricFilters.showCalculated && !metric.isPlanning)) &&
  !isDisabledMetric(metric, nodeName);

export const displayMetric = (
  metric?: number,
  defaultValue?: number | string,
  isDiff?: boolean,
  isRate?: boolean,
  suffix?: string,
) => {
  if (metric || metric === 0) {
    const metricNumber = _.toNumber(metric);
    const displayedMetric = _.round(isRate ? _.multiply(metricNumber, 100) : metricNumber, 1);
    return `${isDiff && displayedMetric > 0 ? '+' : ''}${displayedMetric}${suffix || ''}`;
  }
  return defaultValue;
};

export const isMetricHidden = (
  metric: MetricRowData,
  pipelinesConfig: PipelineConfigMapping | undefined,
  taskPipelineName: string,
  containerNodeName: string,
  nodeName: string,
): boolean =>
  Boolean(
    _.get(
      pipelinesConfig,
      `${taskPipelineName}.${containerNodeName}.${nodeName}.metricKinds.${metric.metricKind}${
        metric.targetNode ? '.targetNodes.' + metric.targetNode : ''
      }.isHidden`,
    ),
  );

const isDisabledMetric = (metric: MetricRowData, nodeName: string): boolean => {
  return (
    DISABLED_METRIC_ROW_OVERRIDES.findIndex(
      d => nodeName === d.nodeName && metric.metricKind === d.metricKind,
    ) > -1
  );
};

export const getEgressErrors = (metrics: MetricRowData[]): boolean[] => {
  const totalEgress = _.find(
    metrics,
    (metric: MetricRowData) => metric.metricKind === NodeMetricKind.VolumeOfEgress,
  );
  const sodInventory = _.find(
    metrics,
    (metric: MetricRowData) => metric.metricKind === NodeMetricKind.StartOfDayInventory,
  );
  const totalIngress = _.find(
    metrics,
    (metric: MetricRowData) => metric.metricKind === NodeMetricKind.VolumeOfIngress,
  );

  if (!totalEgress || !sodInventory || !totalIngress) {
    return [];
  }
  const egressErrors: boolean[] = [];
  const currentDay = moment(new Date()).tz('America/Los_Angeles').startOf('day').toDate();
  totalEgress.data.forEach((day, index) => {
    const dateDiff = day.date.toDate().getTime() - currentDay.getTime();
    if (dateDiff < 0) {
      egressErrors.push(false);
    } else {
      const hasError =
        (day.plan || 0) >
        _.get(sodInventory, `data[${index}].plan`, 0) +
          _.get(totalIngress, `data[${index}].plan`, 0);
      egressErrors.push(hasError);
    }
  });
  return egressErrors;
};

export const filterProjectGroupStatus = (projectGroups: IDailyPlanProjectGroup[] | undefined) => {
  const projectGroupsStatus: { [status: string]: IDailyPlanProjectGroup[] } = {
    [ProjectGroupLifecyle.Production]: [],
    archived: [],
  };
  projectGroups &&
    projectGroups
      .sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1))
      .forEach(projectGroup => {
        const groupStatus = projectGroup.status;
        if (!projectGroupsStatus[groupStatus]) {
          projectGroupsStatus[groupStatus] = [projectGroup];
        } else {
          projectGroupsStatus[groupStatus].push(projectGroup);
        }
      });
  return projectGroupsStatus;
};

export function buildProjectGroupDiff(
  data: UpdateProjectGroupRequest,
  initialValues?: Partial<IDailyPlanProjectGroup> | null,
): ProjectGroupDiff {
  const projectIds: string[] = _.get(initialValues, 'projectIds', []);
  const fields: (
    | keyof ProjectGroupDiff
    | keyof Omit<
        UpdateProjectGroupRequest,
        'ownerIds' | 'projectIds' | 'weeklyThroughputSLA' | 'diff'
      >
  )[] = ['name', 'status', 'slaCadence', 'businessUnitProduct', 'businessUnit', 'description'];
  const diff: ProjectGroupDiff = {
    projectIds: {
      added: data.projectIds.reduce<string[]>((acc, id) => {
        if (!projectIds.includes(id)) {
          acc.push(id);
        }
        return acc;
      }, []),
      removed: projectIds.reduce<string[]>((acc, id) => {
        if (!data.projectIds.includes(id)) {
          acc.push(id);
        }
        return acc;
      }, []),
    },
  };

  for (const field of fields) {
    const value = initialValues?.[field];
    if (data[field] !== value && typeof value !== 'undefined') {
      diff[field] = value as $FIXME;
    }
  }

  return diff;
}
